import { useEffect, useState } from 'react';
import { withJsonFormsControlProps } from '@jsonforms/react';

import isEqual from 'lodash/isEqual';

import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import FormLabel from '@mui/material/FormLabel';

import { ReactComponent as DeleteIcon } from '../../../../../assets/delete-icon.svg';
import { ReactComponent as InfoIcon } from '../../../../../assets/info-icon-light.svg';
import { ReactComponent as PenIcon } from '../../../../../assets/pen-icon.svg';
import { ReactComponent as CheckIcon } from '../../../../../assets/check-icon-black.svg';
import { ReactComponent as CloseIcon } from '../../../../../assets/close-icon-black.svg';

import { palette } from '../../../../../theme/palette';

const Styles = {
  formLabel: {
    display: 'block',
    ml: '4px',
    mb: '4px',
    mt: 1,
  },
  chip: {
    padding: '0px 4px',
    height: '26px',
    fontSize: '14px',
    fontWeight: 400,
    backgroundColor: '#eef3ff',
    margin: '2px',
    borderRadius: '6px',
    maxWidth: '100%',

    '& .MuiChip-label': {
      display: 'block',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: '595px',
      pl: '4px',
      mb: '-1px',
      pr: '10px',
    },
  },
  autocomplete: {
    borderRadius: '8px',

    '& .MuiAutocomplete-tag': {
      margin: '5px',
      borderRadius: '6px',
    },

    '& .Mui-focused': {
      // border: `1px solid ${palette.secondary.main}`,
      border: 'none',
    },
  },
  autocompleteInput: {
    '& .MuiFilledInput-root': {
      backgroundColor: palette.primary.contrastText,
      borderRadius: '8px',
      border: 'none',
      padding: '4px 8px !important',
      color: palette.secondary.dark,

      '&:hover': {
        backgroundColor: palette.primary.contrastText,

        ':not(.Mui-disabled, .Mui-error):before': {
          borderBottom: 'none',
        },
      },
      '&::before': {
        borderBottom: 'none',
      },
      '&::after': {
        borderBottom: 'none',
      },

      '&.Mui-disabled': {
        backgroundColor: '#f5f6fa',
        opacity: 0.5,

        '&:before': {
          borderBottom: 'none',
        },
      },
    },
    '& .Mui-focused': {
      border: 'none',
      backgroundColor: `${palette.primary.contrastText} !important`,
    },
  },
  editInput: {
    height: '26px',
    fontSize: '14px',
    margin: '3px 2px',
    width: '100%',
    '& .MuiInputBase-root': {
      height: '26px',
      fontSize: '14px',
      width: '100%',
    },
    '& .MuiInputBase-input': {
      padding: '0px 6px',
      height: '26px',
    },
    '& .MuiInputBase-sizeSmall': {
      marginTop: 0,
    },
  },
  descWrapper: {
    ml: '4px',
    mb: 1,
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  },
};

const RootTag = (props: any) => {
  return (
    <>
      <FormLabel
        sx={{
          ...Styles.formLabel,
          opacity: props.enabled ? 1 : 0.5,
          ...(props.errors && { color: palette.error.main }),
        }}
        htmlFor='scan-tags'
      >
        {props.label}
      </FormLabel>
      <TagsSelector
        data={props.data}
        disabled={!props.enabled}
        error={props.errors}
        handleChange={(tags: string[]) => props.handleChange(props.path, tags)}
      />
      {props.enabled && props.description && (
        <Box sx={Styles.descWrapper}>
          <InfoIcon style={{ width: '20px', height: '20px' }} />
          <Typography
            sx={{
              fontSize: '14px',
            }}
          >
            {props.description}
          </Typography>
        </Box>
      )}
      {props.enabled && props.errors && (
        <Box sx={Styles.descWrapper}>
          <Typography
            sx={{
              color: palette.error.main,
              fontSize: '14px',
            }}
          >
            {props.errors}
          </Typography>
        </Box>
      )}
    </>
  );
};

function TagsSelector({
  data,
  disabled,
  error,
  handleChange,
}: {
  data: string[] | string | null | undefined;
  disabled?: boolean;
  error?: boolean;
  handleChange: (tags: string[]) => void;
}) {
  const toTagArray = (value: string[] | string | null | undefined): string[] => {
    if (Array.isArray(value)) return value.filter((item) => typeof item === 'string');
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed ? [trimmed] : [];
    }
    return [];
  };

  const [tags, setTags] = useState<string[]>(toTagArray(data));
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');

  useEffect(() => {
    const normalizedData = toTagArray(data);
    if (!isEqual(normalizedData, tags)) {
      setTags(normalizedData);
    }
  }, [data, tags]);

  const handleTagsChange = (newTags: string[]) => {
    setTags(newTags);
    handleChange(newTags);
  };

  const handleEditStart = (index: number, value: string) => {
    setEditingIndex(index);
    setEditingValue(value);
  };

  const handleEditComplete = () => {
    if (editingIndex !== null && editingValue.trim()) {
      const newTags = [...tags];
      newTags[editingIndex] = editingValue.trim();
      handleTagsChange(newTags);
    }
    setEditingIndex(null);
    setEditingValue('');
  };

  const handleEditCancel = () => {
    setEditingIndex(null);
    setEditingValue('');
  };

  const handlePaste = (pastedText: string) => {
    let values: string[] = [];

    if (pastedText.includes('\n')) {
      values = pastedText
        .split('\n')
        .map((val) => val.trim())
        .filter((val) => val !== '');
    } else {
      values = [pastedText.trim()].filter((val) => val !== '');
    }

    if (values.length > 0) {
      const newValues = values.filter((val) => {
        const trimmedVal = val.trim();
        return (
          trimmedVal !== '' &&
          !tags.some((existingTag) => existingTag.trim().toLowerCase() === trimmedVal.toLowerCase())
        );
      });

      if (newValues.length > 0) {
        handleTagsChange([...tags, ...newValues]);
      }
    }
  };

  return (
    <Autocomplete
      open={false}
      multiple
      id='scan-tags'
      options={[]}
      value={tags}
      freeSolo
      disableClearable
      disabled={disabled}
      renderTags={(value: readonly string[], getTagProps) =>
        value.map((option: string, index: number) => {
          const { key, onDelete, ...tagProps } = getTagProps({ index });

          if (editingIndex === index) {
            return (
              <Box
                key={key}
                sx={{
                  display: 'inline-block',
                  margin: '2px',
                  width: `${option.length * (option.length > 20 ? 9.5 : 12) + 32}px`,
                  minWidth: '120px',
                  maxWidth: '600px',
                }}
              >
                <TextField
                  value={editingValue}
                  onChange={(e) => {
                    setEditingValue(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    e.stopPropagation();

                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const ev = editingValue.trim();
                      if ((!tags.includes(ev) && option !== ev) || (tags.includes(ev) && option === ev)) {
                        handleEditComplete();
                      }
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      handleEditCancel();
                    }
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    const input = e.target as HTMLInputElement;
                    input.focus();
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                  }}
                  onFocus={(e) => {
                    e.stopPropagation();
                  }}
                  autoFocus
                  size='small'
                  variant='outlined'
                  sx={Styles.editInput}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end' sx={{ gap: 0.5 }}>
                        <IconButton
                          onClick={() => {
                            handleEditComplete();
                          }}
                          sx={{ p: 0, width: '16px', height: '16px' }}
                        >
                          <CheckIcon style={{ color: palette.primary.main }} />
                        </IconButton>
                        <IconButton
                          onClick={() => {
                            handleEditCancel();
                          }}
                          sx={{ p: 0, width: '16px', height: '16px' }}
                        >
                          <CloseIcon style={{ color: palette.primary.main }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            );
          }

          return (
            <Chip
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography noWrap sx={{ color: 'inherit' }}>
                    {option}
                  </Typography>
                  <IconButton
                    onClick={() => {
                      if (!disabled) {
                        handleEditStart(index, option);
                      }
                    }}
                    sx={{
                      p: 0,
                      width: '16px',
                      height: '16px',
                    }}
                  >
                    <PenIcon style={{ color: palette.primary.main }} />
                  </IconButton>
                </Box>
              }
              onDelete={() => {
                handleTagsChange(tags.filter((tag) => tag !== option));
              }}
              color='primary'
              variant='outlined'
              deleteIcon={
                <DeleteIcon
                  style={{ color: palette.primary.main, width: '16px', height: '16px', marginRight: '2px' }}
                />
              }
              sx={{
                ...Styles.chip,
                cursor: disabled ? 'default' : 'pointer',
                ...(disabled && {
                  color: 'rgba(0, 0, 0, 0.87)',
                  borderColor: 'rgba(0, 0, 0, 0.87)',
                }),

                '&:hover': {
                  backgroundColor: disabled ? '#eef3ff' : '#d6e7ff',
                },
              }}
              key={key}
              {...tagProps}
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          variant='filled'
          disabled={disabled}
          sx={Styles.autocompleteInput}
          onPaste={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const pastedText = e.clipboardData.getData('text');
            handlePaste(pastedText);
            if (params.inputProps) {
              params.inputProps.value = '';
            }
          }}
          onFocus={() => {
            if (editingIndex !== null) {
              handleEditCancel();
              return;
            }
          }}
        />
      )}
      sx={{
        ...Styles.autocomplete,
        border: `1px solid ${error ? palette.error.main : palette.secondary.main}`,
        ...(disabled && {
          backgroundColor: palette.secondary.main,
          opacity: 0.5,
        }),
      }}
      onChange={(event, value) => {
        if (editingIndex !== null) {
          return;
        }

        const keyboardEvent = event as React.KeyboardEvent<Element>;
        if (keyboardEvent.key === 'Backspace') {
          handleTagsChange(tags.slice(0, -1));
          return;
        }

        const newValue = value.length ? value[value.length - 1] : '';
        const values = newValue.trim() ? [newValue.trim()] : [];

        if (Array.isArray(values) && values.length) {
          const newValues = values.filter((val) => {
            const trimmedVal = val.trim();
            return trimmedVal !== '' && !tags.includes(trimmedVal);
          });

          if (newValues.length > 0) {
            handleTagsChange([...tags, ...newValues]);
          }
        }
      }}
    />
  );
}

export default withJsonFormsControlProps(RootTag);
